import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import { X } from 'lucide-preact';
import type { TouchPointViewDto } from '@/types/touchPoint';
import type { LookupOptionDto } from '@/types/lookups';
import { TouchPointType } from '@/types/touchPoint';
import { useLookups } from '@/hooks/useLookups';
import { sanitizeDateTimeInput } from '@/utils/dateTimeFormatters';
import { useAIForm } from '@/hooks/useAIForm';
import type { AIFieldConfig } from '@/hooks/useAIForm';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
import { TextBoxField, Select, DatePicker, FormActions } from '@/components';
import type { SelectOption } from '@/components/common/Select';
import {
  PhonePropertiesSection,
  SocialPropertiesSection,
  MeetingPropertiesSection
} from './touchpoint-sections';

export interface TouchPointFormData {
  Name: string;
  Type: string;
  ScheduledDate: string; // YYYY-MM-DD format for date input (Phone / Social only)
  MeetingType?: string; // Meeting-specific
  TimeZone?: string;
  StartTime?: string; // ISO DateTime string
  EndTime?: string; // ISO DateTime string
  MeetingUrlLink?: string;
  SendWebster?: boolean; // Meeting-specific: arm the Webster Notetaker bot (default true, opt-out)
  NumberOfDials?: number; // Phone-specific
  PostCopy?: string; // Social-specific
  Platform?: string; // Social-specific
  ETag?: string; // Only present when editing
}

interface TouchPointModalProps {
  touchPoint?: TouchPointViewDto | null;
  touchPointTypes: LookupOptionDto[];
  onSave: (data: TouchPointFormData, file?: File) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  fixedType?: string; // When set, type field is locked to this value (e.g., "Meeting")
  isCampaignLevel?: boolean; // True if creating campaign-level touchpoints
  clientCompanyId?: string;
  campaignId: string;
  webId?: string; // Optional: only present for web-level touchpoints
}

// Helper to format Date to YYYY-MM-DD (using UTC to match how we save)
function formatDateOnly(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function TouchPointModal({
  touchPoint,
  touchPointTypes,
  onSave,
  onClose,
  saving,
  fixedType,
  isCampaignLevel,
  campaignId,
}: TouchPointModalProps) {
  const isEditMode = !!touchPoint;
  const { getPlatforms, getCampaignMeetingTypes, getWebMeetingTypes } = useLookups();
  const platforms = getPlatforms();
  const modalRef = useRef<HTMLDivElement>(null);

  // Get appropriate meeting types based on campaign vs web level
  const meetingTypes = isCampaignLevel ? getCampaignMeetingTypes() : getWebMeetingTypes();

  // File upload state (not managed by React Hook Form)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string>(
    () => touchPoint?.MeetingProperties?.Attachment || ''
  );

  // Get initial values
  const getDefaultValues = (): TouchPointFormData => {
    if (touchPoint) {
      return {
        Name: touchPoint.Name || '',
        Type: touchPoint.Type,
        ScheduledDate: touchPoint.PhoneProperties?.ScheduledDate
          ? formatDateOnly(touchPoint.PhoneProperties.ScheduledDate)
          : touchPoint.SocialProperties?.ScheduledDate
          ? formatDateOnly(touchPoint.SocialProperties.ScheduledDate)
          : formatDateOnly(new Date()),
        MeetingType: touchPoint.MeetingProperties?.Type || '',
        TimeZone: touchPoint.MeetingProperties?.TimeZone || '',
        StartTime: sanitizeDateTimeInput(touchPoint.MeetingProperties?.StartTime),
        EndTime: sanitizeDateTimeInput(touchPoint.MeetingProperties?.EndTime),
        MeetingUrlLink: touchPoint.MeetingProperties?.MeetingUrlLink || '',
        NumberOfDials: touchPoint.PhoneProperties?.NumberOfDials || 1,
        PostCopy: touchPoint.SocialProperties?.PostCopy || '',
        Platform: touchPoint.SocialProperties?.Platform || '',
        ETag: touchPoint.ETag,
      };
    }
    return {
      Name: '',
      Type: fixedType || '',
      ScheduledDate: formatDateOnly(new Date()),
      MeetingType: '',
      TimeZone: '',
      StartTime: '',
      EndTime: '',
      MeetingUrlLink: '',
      NumberOfDials: 1,
      PostCopy: '',
      Platform: '',
    };
  };

  // Build dynamic AI fields based on the current touch point type
  // We need the type value before useAIForm, so we track it via state
  const [watchedType, setWatchedType] = useState<string>(
    () => touchPoint?.Type || fixedType || ''
  );

  const aiFields = useMemo<AIFieldConfig[]>(() => {
    const baseFields: AIFieldConfig[] = [
      { aiName: 'touchPointName', formPath: 'Name', displayName: 'Touch Point Name', fieldType: 'text', maxLength: 200 },
    ];

    // Email touchpoints reference an EmailCopy asset — the copy is edited on
    // the asset itself (via Webster), not on the touchpoint, so no email content
    // fields participate in AI form completion here.

    if (watchedType === TouchPointType.Social) {
      baseFields.push(
        { aiName: 'postCopy', formPath: 'PostCopy', displayName: 'Post Copy', fieldType: 'textarea', maxLength: 5000 },
      );
    }

    return baseFields;
  }, [watchedType]);

  // AI-integrated form
  const { form, getFormButtonState } = useAIForm<TouchPointFormData>({
    mode: 'onChange',
    defaultValues: getDefaultValues()
  }, {
    aiContext: {
      campaignId,
      location: ChatLocations.TouchPointEditor,
      entityId: touchPoint?.Id,
      entityType: ChatEntityTypes.TouchPoint,
      viewMode: isEditMode ? ChatViewModes.Edit : ChatViewModes.Create,
    },
    fields: aiFields,
    contextSuppressed: false,
    isEditMode,
  });

  const {
    register,
    control,
    handleSubmit: formHandleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger
  } = form;

  const currentType = watch('Type');
  const currentStartTime = watch('StartTime');

  // Keep watchedType in sync with the form's Type value so aiFields recomputes
  useEffect(() => {
    setWatchedType(currentType);
  }, [currentType]);

  // Update form when touchPoint prop changes (for edit mode)
  useEffect(() => {
    if (touchPoint) {
      const attachmentUrl = touchPoint.MeetingProperties?.Attachment || '';
      setExistingAttachmentUrl(attachmentUrl);
      reset(getDefaultValues());
    } else {
      // Reset file state when creating new touchpoint
      setSelectedFile(null);
      setExistingAttachmentUrl('');
      reset(getDefaultValues());
    }
  }, [touchPoint, fixedType, reset]);

  // Trigger EndTime validation when StartTime changes
  useEffect(() => {
    if (currentStartTime) {
      trigger('EndTime');
    }
  }, [currentStartTime, trigger]);

  // Keyboard handler for ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [saving, onClose]);

  // Convert lookup options to SelectOption format
  const touchPointTypeOptions: SelectOption[] = touchPointTypes.map(type => ({
    value: type.Id,
    label: type.DisplayName
  }));

  const meetingTypeOptions: SelectOption[] = meetingTypes.map(type => ({
    value: type.Description,
    label: type.Description
  }));

  const platformOptions: SelectOption[] = platforms.map(platform => ({
    value: platform.Key,
    label: platform.Description
  }));

  const onSubmit = async (data: TouchPointFormData) => {
    try {
      await onSave(data, selectedFile || undefined);
      onClose();
    } catch (error) {
      console.error('Error saving touchpoint:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md md:max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-600">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            {isEditMode ? 'Edit' : 'Create'} {fixedType === 'Meeting' ? 'Meeting' : 'Touch Point'}
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={formHandleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
            {/* Left Column: Common Fields */}
            <div className="flex-1 space-y-6">
              {/* Touch Point Type */}
              {fixedType ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Touch Point Type <span className="text-red-500">*</span>
                  </label>
                  <div className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    {fixedType}
                  </div>
                  <input type="hidden" {...register('Type')} value={fixedType} />
                </div>
              ) : (
                <Select
                  label="Touch Point Type"
                  required
                  options={touchPointTypeOptions}
                  placeholder={isEditMode ? undefined : "Select a type..."}
                  error={errors.Type?.message}
                  {...register('Type', {
                    required: 'Please select a touch point type',
                    disabled: isEditMode
                  })}
                />
              )}

              {/* Name */}
              <TextBoxField
                control={control}
                name="Name"
                label="Name"
                required
                placeholder="e.g., Welcome Email, Follow-up Call"
                showCharCounter
                maxLength={200}
                rules={{
                  required: 'Name is required',
                  maxLength: { value: 200, message: 'Name must be 200 characters or less' }
                }}
              />

              {/* Scheduled Date - Phone and Social only; Email uses its own Rule editor below */}
              {(currentType === TouchPointType.Phone ||
                currentType === TouchPointType.Social) && (
                <DatePicker
                  label="Scheduled Date"
                  required
                  error={errors.ScheduledDate?.message}
                  {...register('ScheduledDate', {
                    required: 'Scheduled date is required'
                  })}
                />
              )}
            </div>

            {/* Right Column: Type-Specific Fields */}
            <div className="flex-1 space-y-6">
              {currentType && (
                <>

                  {/* Phone-specific fields */}
                  {currentType === TouchPointType.Phone && (
                    <PhonePropertiesSection
                      register={register}
                      errors={errors}
                    />
                  )}

                  {/* Social-specific fields */}
                  {currentType === TouchPointType.Social && (
                    <SocialPropertiesSection
                      control={control}
                      register={register}
                      errors={errors}
                      platformOptions={platformOptions}
                    />
                  )}

                  {/* Meeting-specific fields */}
                  {currentType === TouchPointType.Meeting && (
                    <MeetingPropertiesSection
                      control={control}
                      register={register}
                      errors={errors}
                      meetingTypeOptions={meetingTypeOptions}
                      currentStartTime={currentStartTime}
                      selectedFile={selectedFile}
                      setSelectedFile={setSelectedFile}
                      existingAttachmentUrl={existingAttachmentUrl}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <FormActions
            onCancel={onClose}
            isSubmitting={saving}
            cancelText="Cancel"
            {...getFormButtonState(saving, !!selectedFile)}
          />
          </div>
        </form>
      </div>
    </div>
  );
}
